import { ADD_ACCESS_TO_GROUP } from '@/common/actions/create';
import { DELETE_ACCESS_GROUP_RESOURCE } from '@/common/actions/delete';
import { GET_ACCESS_GROUP_RESOURCES } from '@/common/actions/get';
import { SectionHead, TableFiltersBar } from '@/common/pages/accountEdit/usersAndGroups/components';
import { ModalAddItems } from '@/common/pages/accountEdit/usersAndGroups/modals/ModalAddItems';
import { getVariables, queryParamsInitial } from '@/common/pages/accountEdit/usersAndGroups/store';
import { useTableQueryParams } from '@/common/pages/accountEdit/usersAndGroups/store/useTableQueryParams';
import { resourceNameFiltersConfig } from '@/common/pages/accountEdit/usersAndGroups/utils/tableFilters';
import { COLORS, OrderTypes } from '@/constants';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box, ButtonIcon, ICONS_NAME, Table, Tabs } from '@servicepipe/ui-components';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const ALL_RESOURCES_LIMIT = 3000;

export const AccessGroupResourcesPage = ({
  title,
  addButtonLabel,
  resourceType,
  filterRemap,
  accountQuery,
  accountItemsPath,
  extraAccountVariables = {},
  accountQueryVariables,
  getItemId = (item) => item.id,
  getItemPrimary,
  getItemSecondary,
  innerTabs,
  defaultResourceTab,
}) => {
  const { id: accountId, groupId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedInnerTab =
    searchParams.get('resourceTab') || defaultResourceTab || innerTabs?.[0]?.key;
  const { queryParams, onPageChange, onSortingChange, onFiltersChange } = useTableQueryParams({
    ...queryParamsInitial,
    sortKey: 'resourceId',
  });
  const [isAddModalShow, setIsAddModalShow] = useState(false);
  const [removingResourceId, setRemovingResourceId] = useState(null);

  const [getAccessGroupResources, { data, loading }] = useLazyQuery(GET_ACCESS_GROUP_RESOURCES, {
    fetchPolicy: 'network-only',
  });

  const { data: accountResourcesData, loading: accountResourcesLoading } = useQuery(accountQuery, {
    variables: accountQueryVariables || {
      page: 1,
      pageSize: ALL_RESOURCES_LIMIT,
      limit: ALL_RESOURCES_LIMIT,
      filterPartnerClientAccountId: Number(accountId),
      partnerClientAccountId: Number(accountId),
      deletedAt: null,
      deletedAtEq: null,
      ...extraAccountVariables,
    },
    skip: !isAddModalShow || !accountId,
    fetchPolicy: 'network-only',
  });

  const { data: groupResourcesData } = useQuery(GET_ACCESS_GROUP_RESOURCES, {
    variables: {
      limit: ALL_RESOURCES_LIMIT,
      page: 1,
      filterAccessGroupId: Number(groupId),
      filterResourceType: resourceType,
      deletedAt: null,
    },
    skip: !isAddModalShow || !groupId,
    fetchPolicy: 'network-only',
  });

  const [addAccessToGroup, { loading: adding }] = useMutation(ADD_ACCESS_TO_GROUP, {
    onError: (error) => toast.error(error.message),
  });
  const [deleteAccessGroupResource] = useMutation(DELETE_ACCESS_GROUP_RESOURCE, {
    onError: (error) => toast.error(error.message),
  });

  const fetchResources = () => {
    let variables = {
      ...getVariables(queryParams, { filterResourceName: 'string' }, { pageSizeKey: 'limit' }),
      filterAccessGroupId: Number(groupId),
      filterResourceType: resourceType,
      deletedAt: null,
    };

    if (variables.filterResourceName && filterRemap) {
      variables[filterRemap] = variables.filterResourceName;
      delete variables.filterResourceName;
    }

    return getAccessGroupResources({ variables });
  };

  useEffect(() => {
    if (!groupId) {
      return;
    }

    fetchResources();
  }, [queryParams, groupId, resourceType]);

  const handleRemoveResource = async (resourceId) => {
    setRemovingResourceId(resourceId);

    try {
      await deleteAccessGroupResource({
        variables: {
          input: {
            accessGroupId: Number(groupId),
            resourceId,
            resourceType,
          },
        },
      });
      toast.success('Success');
      fetchResources();
    } finally {
      setRemovingResourceId(null);
    }
  };

  const sortingInitial = {
    id: queryParams.sortKey || '',
    desc: queryParams.sortDirection === OrderTypes.Desc,
  };

  const columns = [
    {
      id: 'resourceId',
      width: 120,
      minWidth: 120,
      header: 'ID',
      body: 'resourceId',
      enableSorting: true,
    },
    {
      id: 'resourceName',
      header: 'Resource name',
      minWidth: 250,
      width: 250,
      body: 'resourceName',
    },
    {
      id: 'actions',
      width: 50,
      minWidth: 50,
      meta: { justify: 'center', variant: 'action' },
      header: '',
      body: ({ row }) => {
        const resourceId = row.original.resourceId;
        return (
          <Box width="100%" justify="flex-end">
            <ButtonIcon
              size="small"
              variant="ghost"
              icon={ICONS_NAME.delete}
              isDisabled={removingResourceId === resourceId}
              iconColor={COLORS.grayDark}
              onClick={() => handleRemoveResource(resourceId)}
            />
          </Box>
        );
      },
    },
  ];

  const accountItems = accountResourcesData?.[accountItemsPath]?.items || [];
  const alreadyAddedIds = (groupResourcesData?.accessGroupResources?.items || []).map(
    (item) => item.resourceId
  );

  return (
    <Box gap="24px" direction="column">
      {innerTabs?.length > 1 && (
        <Tabs
          tabs={innerTabs}
          selectedTab={selectedInnerTab}
          setTab={(value) => {
            const next = new URLSearchParams(searchParams);
            next.set('resourceTab', value);
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      <SectionHead
        title={title}
        buttonLabel={addButtonLabel}
        onButtonClick={() => setIsAddModalShow(true)}
      />

      <Box direction="column" width="100%" minWidth="0">
        <TableFiltersBar
          filtersConfig={resourceNameFiltersConfig}
          queryFilters={queryParams.filters ?? []}
          onFiltersChange={onFiltersChange}
        />

        <Table
          loading={loading}
          data={data?.accessGroupResources?.items || []}
          columns={columns}
          page={queryParams.page}
          size={queryParams.pageSize}
          totalPages={data?.accessGroupResources?.info?.totalPages || 1}
          totalCount={data?.accessGroupResources?.info?.totalCount || 0}
          onPageChange={onPageChange}
          onSortingChange={onSortingChange}
          initialStateSort={sortingInitial}
        />
      </Box>

      <ModalAddItems
        isShow={isAddModalShow}
        title={addButtonLabel}
        searchPlaceholder="Name"
        items={accountItems}
        alreadyAddedIds={alreadyAddedIds}
        loading={accountResourcesLoading}
        getId={getItemId}
        getPrimary={getItemPrimary}
        getSecondary={getItemSecondary}
        onClose={() => setIsAddModalShow(false)}
        submitting={adding}
        onAdd={async (resourceIds) => {
          await addAccessToGroup({
            variables: {
              input: {
                accessGroupId: Number(groupId),
                resourceIds: resourceIds.map(Number),
                resourceType,
              },
            },
          });
          toast.success('Success');
          setIsAddModalShow(false);
          fetchResources();
        }}
      />
    </Box>
  );
};
